import React, { useState } from "react";

function SQLGuide() {
    const [copiedCode, setCopiedCode] = useState<string | null>(null);

    const copyToClipboard = (code: string, id: string) => {
        navigator.clipboard.writeText(code);
        setCopiedCode(id);
        setTimeout(() => setCopiedCode(null), 2000);
    };

    const codeBlockStyle = {
        backgroundColor: '#1e293b',
        color: '#e2e8f0',
        padding: '1.5rem',
        borderRadius: '8px',
        fontSize: '0.9rem',
        fontFamily: 'Monaco, Consolas, "Courier New", monospace',
        overflowX: 'auto' as const,
        position: 'relative' as const,
        marginBottom: '1rem'
    };

    const copyButtonStyle = {
        position: 'absolute' as const,
        top: '0.5rem',
        right: '0.5rem',
        backgroundColor: '#475569',
        color: '#e2e8f0',
        border: 'none',
        borderRadius: '4px',
        padding: '0.4rem 0.8rem',
        fontSize: '0.75rem',
        cursor: 'pointer',
        transition: 'background-color 0.2s'
    };

    return (
        <div style={{
            minHeight: '100vh',
            backgroundColor: '#F9F9F9',
            paddingBottom: '4rem'
        }}>
            {/* Hero Section */}
            <div style={{
                backgroundColor: '#F9F9F9',
                paddingTop: '5rem',
                paddingBottom: '3rem',
                marginBottom: '3rem'
            }}>
                <div className="container">
                    <div className="row justify-content-center text-center">
                        <div className="col-lg-8">
                            <h1 style={{
                                fontSize: '3.5rem',
                                fontWeight: 'bold',
                                color: '#1A2940',
                                marginBottom: '1.5rem'
                            }}>
                                SQL Guide for MintDB
                            </h1>
                            <p style={{
                                fontSize: '1.15rem',
                                color: 'grey',
                                lineHeight: '1.7',
                                maxWidth: '700px',
                                margin: '0 auto'
                            }}>
                                Learn how to query the MintDB database using SQL. This guide covers basic SQL syntax and provides practical examples for neuropsychiatric mutation research.
                            </p>
                        </div>
                    </div>
                </div>
            </div>

            <div className="container">

                {/* Main Content */}
                <div className="row justify-content-center">
                    <div className="col-lg-10">

                        {/* Introduction */}
                        <section style={{ marginBottom: '4rem' }}>
                            <h2 className="analysisHeader" style={{
                                fontSize: '2rem',
                                fontWeight: 'bold',
                                color: '#1A2940',
                                marginBottom: '1.5rem'
                            }}>
                                What is SQL?
                            </h2>
                            <p style={{
                                color: 'grey',
                                fontSize: '1.05rem',
                                lineHeight: '1.8',
                                marginBottom: '1rem'
                            }}>
                                SQL (Structured Query Language) is a standard programming language used to communicate with databases. It allows you to retrieve, filter, and analyze data using intuitive, English-like commands. No prior programming experience is required!
                            </p>
                            <p style={{
                                color: 'grey',
                                fontSize: '1.05rem',
                                lineHeight: '1.8',
                                marginBottom: '0'
                            }}>
                                With SQL, you can perform complex searches across MintDB's neuropsychiatric mutation data, including genetic variants, protein domains, AlphaMissense predictions, and Biobank associations.
                            </p>
                        </section>

                        {/* Basic SQL Structure */}
                        <section style={{ marginBottom: '4rem' }}>
                            <h2 className="analysisHeader" style={{
                                fontSize: '2rem',
                                fontWeight: 'bold',
                                color: '#1A2940',
                                marginBottom: '1.5rem'
                            }}>
                                Basic SQL Query Structure
                            </h2>
                            <p style={{
                                color: 'grey',
                                fontSize: '1.05rem',
                                lineHeight: '1.8',
                                marginBottom: '1.5rem'
                            }}>
                                Every SQL query follows a basic structure. Here's the most common format:
                            </p>
                            <div style={codeBlockStyle}>
                                <button
                                    style={copyButtonStyle}
                                    onClick={() => copyToClipboard('SELECT column1, column2\nFROM table_name\nWHERE condition;', 'basic')}
                                    onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#60a5fa'}
                                    onMouseLeave={(e) => e.currentTarget.style.backgroundColor = '#475569'}
                                >
                                    {copiedCode === 'basic' ? 'Copied!' : 'Copy'}
                                </button>
                                <pre style={{ margin: 0 }}>
{`SELECT column1, column2
FROM table_name
WHERE condition;`}
                                </pre>
                            </div>
                            <ul style={{
                                color: 'grey',
                                fontSize: '1rem',
                                lineHeight: '2',
                                marginTop: '1.5rem',
                                paddingLeft: '1.5rem'
                            }}>
                                <li><strong style={{ color: '#1A2940', fontWeight: 'bold' }}>SELECT</strong> - Specifies which columns you want to retrieve</li>
                                <li><strong style={{ color: '#1A2940', fontWeight: 'bold' }}>FROM</strong> - Specifies which table to query</li>
                                <li><strong style={{ color: '#1A2940', fontWeight: 'bold' }}>WHERE</strong> - Filters results based on conditions (optional)</li>
                            </ul>
                        </section>

                        {/* MintDB Tables */}
                        <section style={{ marginBottom: '4rem' }}>
                            <h2 className="analysisHeader" style={{
                                fontSize: '2rem',
                                fontWeight: 'bold',
                                color: '#1A2940',
                                marginBottom: '1.5rem'
                            }}>
                                MintDB Tables and Columns
                            </h2>
                            <p style={{
                                color: 'grey',
                                fontSize: '1.05rem',
                                lineHeight: '1.8',
                                marginBottom: '2rem'
                            }}>
                                MintDB contains several key tables with different types of data:
                            </p>

                            <div style={{ marginBottom: '2rem', borderLeft: '3px solid #e2e8f0', paddingLeft: '1.5rem' }}>
                                <h4 style={{ color: '#1A2940', marginBottom: '0.5rem', fontSize: '1.25rem', fontWeight: '600' }}>targets</h4>
                                <p style={{ color: '#64748b', fontSize: '0.95rem', marginBottom: '0.75rem', lineHeight: '1.6' }}>
                                    Protein or gene targets related to drugs and PDB structures
                                </p>
                                <code style={{
                                    backgroundColor: '#f1f5f9',
                                    padding: '0.5rem 0.75rem',
                                    borderRadius: '4px',
                                    fontSize: '0.9rem',
                                    color: '#334155',
                                    display: 'inline-block'
                                }}>
                                    id, drug_id, target_name, gene_name, pdb_id, uniprot_id
                                </code>
                            </div>

                            <div style={{ marginBottom: '2rem', borderLeft: '3px solid #e2e8f0', paddingLeft: '1.5rem' }}>
                                <h4 style={{ color: '#1A2940', marginBottom: '0.5rem', fontSize: '1.25rem', fontWeight: '600' }}>drugs</h4>
                                <p style={{ color: '#64748b', fontSize: '0.95rem', marginBottom: '0.75rem', lineHeight: '1.6' }}>
                                    Drug information linked to specific disorders
                                </p>
                                <code style={{
                                    backgroundColor: '#f1f5f9',
                                    padding: '0.5rem 0.75rem',
                                    borderRadius: '4px',
                                    fontSize: '0.9rem',
                                    color: '#334155',
                                    display: 'inline-block'
                                }}>
                                    id, disorder_id, drug_name
                                </code>
                            </div>

                            <div style={{ marginBottom: '2rem', borderLeft: '3px solid #e2e8f0', paddingLeft: '1.5rem' }}>
                                <h4 style={{ color: '#1A2940', marginBottom: '0.5rem', fontSize: '1.25rem', fontWeight: '600' }}>disorders</h4>
                                <p style={{ color: '#64748b', fontSize: '0.95rem', marginBottom: '0.75rem', lineHeight: '1.6' }}>
                                    List of diseases or conditions targeted by drugs
                                </p>
                                <code style={{
                                    backgroundColor: '#f1f5f9',
                                    padding: '0.5rem 0.75rem',
                                    borderRadius: '4px',
                                    fontSize: '0.9rem',
                                    color: '#334155',
                                    display: 'inline-block'
                                }}>
                                    id, name
                                </code>
                            </div>

                            <div style={{ marginBottom: '2rem', borderLeft: '3px solid #e2e8f0', paddingLeft: '1.5rem' }}>
                                <h4 style={{ color: '#1A2940', marginBottom: '0.5rem', fontSize: '1.25rem', fontWeight: '600' }}>rsids</h4>
                                <p style={{ color: '#64748b', fontSize: '0.95rem', marginBottom: '0.75rem', lineHeight: '1.6' }}>
                                    SNP identifiers (rsIDs) associated with disorders, drugs, and targets
                                </p>
                                <code style={{
                                    backgroundColor: '#f1f5f9',
                                    padding: '0.5rem 0.75rem',
                                    borderRadius: '4px',
                                    fontSize: '0.9rem',
                                    color: '#334155',
                                    display: 'inline-block'
                                }}>
                                    id, disorder_id, drug_id, target_id, rsid, uk_biobank
                                </code>
                            </div>

                            <div style={{ marginBottom: '2rem', borderLeft: '3px solid #e2e8f0', paddingLeft: '1.5rem' }}>
                                <h4 style={{ color: '#1A2940', marginBottom: '0.5rem', fontSize: '1.25rem', fontWeight: '600' }}>mutations</h4>
                                <p style={{ color: '#64748b', fontSize: '0.95rem', marginBottom: '0.75rem', lineHeight: '1.6' }}>
                                    Gene or protein mutations mapped to rsIDs with AlphaMissense classifications
                                </p>
                                <code style={{
                                    backgroundColor: '#f1f5f9',
                                    padding: '0.5rem 0.75rem',
                                    borderRadius: '4px',
                                    fontSize: '0.9rem',
                                    color: '#334155',
                                    display: 'inline-block'
                                }}>
                                    id, rsid, mutation, position, original_aa, mutated_aa, classification, alphamissense_report
                                </code>
                            </div>

                            <div style={{ marginBottom: '2rem', borderLeft: '3px solid #e2e8f0', paddingLeft: '1.5rem' }}>
                                <h4 style={{ color: '#1A2940', marginBottom: '0.5rem', fontSize: '1.25rem', fontWeight: '600' }}>domains</h4>
                                <p style={{ color: '#64748b', fontSize: '0.95rem', marginBottom: '0.75rem', lineHeight: '1.6' }}>
                                    Protein domain annotations linked to UniProt identifiers
                                </p>
                                <code style={{
                                    backgroundColor: '#f1f5f9',
                                    padding: '0.5rem 0.75rem',
                                    borderRadius: '4px',
                                    fontSize: '0.9rem',
                                    color: '#334155',
                                    display: 'inline-block'
                                }}>
                                    id, uniprot_id, feature_type, description, start_position, end_position
                                </code>
                            </div>

                            <div style={{ marginBottom: '2rem', borderLeft: '3px solid #e2e8f0', paddingLeft: '1.5rem' }}>
                                <h4 style={{ color: '#1A2940', marginBottom: '0.5rem', fontSize: '1.25rem', fontWeight: '600' }}>gene_expression</h4>
                                <p style={{ color: '#64748b', fontSize: '0.95rem', marginBottom: '0.75rem', lineHeight: '1.6' }}>
                                    Expression levels of genes across different brain regions
                                </p>
                                <code style={{
                                    backgroundColor: '#f1f5f9',
                                    padding: '0.5rem 0.75rem',
                                    borderRadius: '4px',
                                    fontSize: '0.9rem',
                                    color: '#334155',
                                    display: 'inline-block'
                                }}>
                                    id, gene_id, gene_name, brain_region, tpm, ptmp, ntmp
                                </code>
                            </div>

                            <div style={{ marginBottom: '2rem', borderLeft: '3px solid #e2e8f0', paddingLeft: '1.5rem' }}>
                                <h4 style={{ color: '#1A2940', marginBottom: '0.5rem', fontSize: '1.25rem', fontWeight: '600' }}>binding_site_analysis</h4>
                                <p style={{ color: '#64748b', fontSize: '0.95rem', marginBottom: '0.75rem', lineHeight: '1.6' }}>
                                    PDB binding sites and mutations affecting binding
                                </p>
                                <code style={{
                                    backgroundColor: '#f1f5f9',
                                    padding: '0.5rem 0.75rem',
                                    borderRadius: '4px',
                                    fontSize: '0.9rem',
                                    color: '#334155',
                                    display: 'inline-block'
                                }}>
                                    id, target_id, gene_name, pdb_id, residue_count, mutation_count, binding_mutations
                                </code>
                            </div>

                            <div style={{ borderLeft: '3px solid #e2e8f0', paddingLeft: '1.5rem' }}>
                                <h4 style={{ color: '#1A2940', marginBottom: '0.5rem', fontSize: '1.25rem', fontWeight: '600' }}>ligands</h4>
                                <p style={{ color: '#64748b', fontSize: '0.95rem', marginBottom: '0.75rem', lineHeight: '1.6' }}>
                                    Ligand names and their associated genes
                                </p>
                                <code style={{
                                    backgroundColor: '#f1f5f9',
                                    padding: '0.5rem 0.75rem',
                                    borderRadius: '4px',
                                    fontSize: '0.9rem',
                                    color: '#334155',
                                    display: 'inline-block'
                                }}>
                                    id, gene_name, ligand_name
                                </code>
                            </div>
                        </section>

                        {/* Example Queries */}
                        <section style={{ marginBottom: '4rem' }}>
                            <h2 className="analysisHeader" style={{
                                fontSize: '2rem',
                                fontWeight: 'bold',
                                color: '#1A2940',
                                marginBottom: '2rem'
                            }}>
                                Example Queries
                            </h2>

                            {/* Example 1 */}
                            <div style={{
                                marginBottom: '3rem',
                                paddingBottom: '2rem',
                                borderBottom: '1px solid #dee2e6'
                            }}>
                                <h4 style={{ color: '#1A2940', marginBottom: '0.75rem', fontSize: '1.35rem', fontWeight: 'bold' }}>1. Select All Targets for a Specific Gene</h4>
                                <p style={{ color: 'grey', fontSize: '1rem', marginBottom: '1.25rem', lineHeight: '1.6' }}>
                                    Retrieve all targets for the SCN1A gene:
                                </p>
                                <div style={codeBlockStyle}>
                                    <button
                                        style={copyButtonStyle}
                                        onClick={() => copyToClipboard("SELECT * FROM targets WHERE gene_name = 'SCN1A';", 'ex1')}
                                        onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#60a5fa'}
                                        onMouseLeave={(e) => e.currentTarget.style.backgroundColor = '#475569'}
                                    >
                                        {copiedCode === 'ex1' ? 'Copied!' : 'Copy'}
                                    </button>
                                    <pre style={{ margin: 0 }}>
{`SELECT * FROM targets WHERE gene_name = 'SCN1A';`}
                                    </pre>
                                </div>
                            </div>

                            {/* Example 2 */}
                            <div style={{
                                marginBottom: '3rem',
                                paddingBottom: '2rem',
                                borderBottom: '1px solid #dee2e6'
                            }}>
                                <h4 style={{ color: '#1A2940', marginBottom: '0.75rem', fontSize: '1.35rem', fontWeight: 'bold' }}>2. Find Drugs for a Specific Disorder</h4>
                                <p style={{ color: 'grey', fontSize: '1rem', marginBottom: '1.25rem', lineHeight: '1.6' }}>
                                    Find all drugs associated with ADHD:
                                </p>
                                <div style={codeBlockStyle}>
                                    <button
                                        style={copyButtonStyle}
                                        onClick={() => copyToClipboard("SELECT d.drug_name, dis.name as disorder_name\nFROM drugs d\nJOIN disorders dis ON d.disorder_id = dis.id\nWHERE dis.name = 'ADHD';", 'ex2')}
                                        onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#60a5fa'}
                                        onMouseLeave={(e) => e.currentTarget.style.backgroundColor = '#475569'}
                                    >
                                        {copiedCode === 'ex2' ? 'Copied!' : 'Copy'}
                                    </button>
                                    <pre style={{ margin: 0 }}>
{`SELECT d.drug_name, dis.name as disorder_name
FROM drugs d
JOIN disorders dis ON d.disorder_id = dis.id
WHERE dis.name = 'ADHD';`}
                                    </pre>
                                </div>
                            </div>

                            {/* Example 3 */}
                            <div style={{
                                marginBottom: '3rem',
                                paddingBottom: '2rem',
                                borderBottom: '1px solid #dee2e6'
                            }}>
                                <h4 style={{ color: '#1A2940', marginBottom: '0.75rem', fontSize: '1.35rem', fontWeight: 'bold' }}>3. Join Multiple Tables</h4>
                                <p style={{ color: 'grey', fontSize: '1rem', marginBottom: '1.25rem', lineHeight: '1.6' }}>
                                    Get targets with their drugs and disorders:
                                </p>
                                <div style={codeBlockStyle}>
                                    <button
                                        style={copyButtonStyle}
                                        onClick={() => copyToClipboard("SELECT t.target_name, t.gene_name, d.drug_name, dis.name AS disorder\nFROM targets t\nJOIN drugs d ON t.drug_id = d.id\nJOIN disorders dis ON d.disorder_id = dis.id;", 'ex3')}
                                        onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#60a5fa'}
                                        onMouseLeave={(e) => e.currentTarget.style.backgroundColor = '#475569'}
                                    >
                                        {copiedCode === 'ex3' ? 'Copied!' : 'Copy'}
                                    </button>
                                    <pre style={{ margin: 0 }}>
{`SELECT t.target_name, t.gene_name, d.drug_name, dis.name AS disorder
FROM targets t
JOIN drugs d ON t.drug_id = d.id
JOIN disorders dis ON d.disorder_id = dis.id;`}
                                    </pre>
                                </div>
                            </div>

                            {/* Example 4 */}
                            <div style={{
                                marginBottom: '3rem',
                                paddingBottom: '2rem',
                                borderBottom: '1px solid #dee2e6'
                            }}>
                                <h4 style={{ color: '#1A2940', marginBottom: '0.75rem', fontSize: '1.35rem', fontWeight: 'bold' }}>4. Query Mutations with rsIDs</h4>
                                <p style={{ color: 'grey', fontSize: '1rem', marginBottom: '1.25rem', lineHeight: '1.6' }}>
                                    Find mutations for a specific rsID:
                                </p>
                                <div style={codeBlockStyle}>
                                    <button
                                        style={copyButtonStyle}
                                        onClick={() => copyToClipboard("SELECT m.*, r.uk_biobank\nFROM mutations m\nJOIN rsids r ON m.rsid = r.rsid\nWHERE m.rsid = 'rs1234567';", 'ex4')}
                                        onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#60a5fa'}
                                        onMouseLeave={(e) => e.currentTarget.style.backgroundColor = '#475569'}
                                    >
                                        {copiedCode === 'ex4' ? 'Copied!' : 'Copy'}
                                    </button>
                                    <pre style={{ margin: 0 }}>
{`SELECT m.*, r.uk_biobank
FROM mutations m
JOIN rsids r ON m.rsid = r.rsid
WHERE m.rsid = 'rs1234567';`}
                                    </pre>
                                </div>
                            </div>

                            {/* Example 5 */}
                            <div style={{
                                marginBottom: '3rem',
                                paddingBottom: '2rem',
                                borderBottom: '1px solid #dee2e6'
                            }}>
                                <h4 style={{ color: '#1A2940', marginBottom: '0.75rem', fontSize: '1.35rem', fontWeight: 'bold' }}>5. Count Targets per Disorder</h4>
                                <p style={{ color: 'grey', fontSize: '1rem', marginBottom: '1.25rem', lineHeight: '1.6' }}>
                                    Use aggregation functions to count targets in each disorder:
                                </p>
                                <div style={codeBlockStyle}>
                                    <button
                                        style={copyButtonStyle}
                                        onClick={() => copyToClipboard("SELECT dis.name, COUNT(DISTINCT t.id) AS target_count\nFROM disorders dis\nJOIN drugs d ON dis.id = d.disorder_id\nJOIN targets t ON d.id = t.drug_id\nGROUP BY dis.name\nORDER BY target_count DESC;", 'ex5')}
                                        onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#60a5fa'}
                                        onMouseLeave={(e) => e.currentTarget.style.backgroundColor = '#475569'}
                                    >
                                        {copiedCode === 'ex5' ? 'Copied!' : 'Copy'}
                                    </button>
                                    <pre style={{ margin: 0 }}>
{`SELECT dis.name, COUNT(DISTINCT t.id) AS target_count
FROM disorders dis
JOIN drugs d ON dis.id = d.disorder_id
JOIN targets t ON d.id = t.drug_id
GROUP BY dis.name
ORDER BY target_count DESC;`}
                                    </pre>
                                </div>
                            </div>

                            {/* Example 6 */}
                            <div style={{
                                marginBottom: '3rem',
                                paddingBottom: '2rem',
                                borderBottom: '1px solid #dee2e6'
                            }}>
                                <h4 style={{ color: '#1A2940', marginBottom: '0.75rem', fontSize: '1.35rem', fontWeight: 'bold' }}>6. Query Binding Site Analysis</h4>
                                <p style={{ color: 'grey', fontSize: '1rem', marginBottom: '1.25rem', lineHeight: '1.6' }}>
                                    Find targets with binding mutations:
                                </p>
                                <div style={codeBlockStyle}>
                                    <button
                                        style={copyButtonStyle}
                                        onClick={() => copyToClipboard("SELECT gene_name, pdb_id, binding_mutation_count\nFROM binding_site_analysis\nWHERE binding_mutation_count > 0\nORDER BY binding_mutation_count DESC;", 'ex6')}
                                        onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#60a5fa'}
                                        onMouseLeave={(e) => e.currentTarget.style.backgroundColor = '#475569'}
                                    >
                                        {copiedCode === 'ex6' ? 'Copied!' : 'Copy'}
                                    </button>
                                    <pre style={{ margin: 0 }}>
{`SELECT gene_name, pdb_id, binding_mutation_count
FROM binding_site_analysis
WHERE binding_mutation_count > 0
ORDER BY binding_mutation_count DESC;`}
                                    </pre>
                                </div>
                            </div>

                            {/* Example 7 */}
                            <div style={{
                                marginBottom: '3rem',
                                paddingBottom: '2rem',
                                borderBottom: '1px solid #dee2e6'
                            }}>
                                <h4 style={{ color: '#1A2940', marginBottom: '0.75rem', fontSize: '1.35rem', fontWeight: 'bold' }}>7. Query Gene Expression in Brain Regions</h4>
                                <p style={{ color: 'grey', fontSize: '1rem', marginBottom: '1.25rem', lineHeight: '1.6' }}>
                                    Find gene expression levels in specific brain regions:
                                </p>
                                <div style={codeBlockStyle}>
                                    <button
                                        style={copyButtonStyle}
                                        onClick={() => copyToClipboard("SELECT gene_name, brain_region, tpm\nFROM gene_expression\nWHERE gene_name = 'DRD2' AND tpm > 10\nORDER BY tpm DESC;", 'ex7')}
                                        onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#60a5fa'}
                                        onMouseLeave={(e) => e.currentTarget.style.backgroundColor = '#475569'}
                                    >
                                        {copiedCode === 'ex7' ? 'Copied!' : 'Copy'}
                                    </button>
                                    <pre style={{ margin: 0 }}>
{`SELECT gene_name, brain_region, tpm
FROM gene_expression
WHERE gene_name = 'DRD2' AND tpm > 10
ORDER BY tpm DESC;`}
                                    </pre>
                                </div>
                            </div>

                            {/* Example 8 */}
                            <div style={{
                                marginBottom: '2rem'
                            }}>
                                <h4 style={{ color: '#1A2940', marginBottom: '0.75rem', fontSize: '1.35rem', fontWeight: 'bold' }}>8. Find Ligands for Specific Genes</h4>
                                <p style={{ color: 'grey', fontSize: '1rem', marginBottom: '1.25rem', lineHeight: '1.6' }}>
                                    Retrieve ligands associated with a gene:
                                </p>
                                <div style={codeBlockStyle}>
                                    <button
                                        style={copyButtonStyle}
                                        onClick={() => copyToClipboard("SELECT gene_name, ligand_name\nFROM ligands\nWHERE gene_name = 'DRD2'\nLIMIT 10;", 'ex8')}
                                        onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#60a5fa'}
                                        onMouseLeave={(e) => e.currentTarget.style.backgroundColor = '#475569'}
                                    >
                                        {copiedCode === 'ex8' ? 'Copied!' : 'Copy'}
                                    </button>
                                    <pre style={{ margin: 0 }}>
{`SELECT gene_name, ligand_name
FROM ligands
WHERE gene_name = 'DRD2'
LIMIT 10;`}
                                    </pre>
                                </div>
                            </div>
                        </section>

                        {/* Common SQL Keywords */}
                        <section style={{ marginBottom: '4rem' }}>
                            <h2 className="analysisHeader" style={{
                                fontSize: '2rem',
                                fontWeight: 'bold',
                                color: '#1A2940',
                                marginBottom: '2rem'
                            }}>
                                Common SQL Keywords
                            </h2>
                            <div style={{
                                display: 'grid',
                                gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))',
                                gap: '2rem'
                            }}>
                                <div>
                                    <h5 style={{ color: '#1A2940', marginBottom: '0.5rem', fontSize: '1.1rem', fontWeight: 'bold' }}>SELECT</h5>
                                    <p style={{ color: 'grey', fontSize: '0.95rem', margin: 0, lineHeight: '1.6' }}>Retrieve data from columns</p>
                                </div>
                                <div>
                                    <h5 style={{ color: '#1A2940', marginBottom: '0.5rem', fontSize: '1.1rem', fontWeight: 'bold' }}>FROM</h5>
                                    <p style={{ color: 'grey', fontSize: '0.95rem', margin: 0, lineHeight: '1.6' }}>Specify which table to query</p>
                                </div>
                                <div>
                                    <h5 style={{ color: '#1A2940', marginBottom: '0.5rem', fontSize: '1.1rem', fontWeight: 'bold' }}>WHERE</h5>
                                    <p style={{ color: 'grey', fontSize: '0.95rem', margin: 0, lineHeight: '1.6' }}>Filter results by conditions</p>
                                </div>
                                <div>
                                    <h5 style={{ color: '#1A2940', marginBottom: '0.5rem', fontSize: '1.1rem', fontWeight: 'bold' }}>JOIN</h5>
                                    <p style={{ color: 'grey', fontSize: '0.95rem', margin: 0, lineHeight: '1.6' }}>Combine data from multiple tables</p>
                                </div>
                                <div>
                                    <h5 style={{ color: '#1A2940', marginBottom: '0.5rem', fontSize: '1.1rem', fontWeight: 'bold' }}>GROUP BY</h5>
                                    <p style={{ color: 'grey', fontSize: '0.95rem', margin: 0, lineHeight: '1.6' }}>Group rows by column values</p>
                                </div>
                                <div>
                                    <h5 style={{ color: '#1A2940', marginBottom: '0.5rem', fontSize: '1.1rem', fontWeight: 'bold' }}>ORDER BY</h5>
                                    <p style={{ color: 'grey', fontSize: '0.95rem', margin: 0, lineHeight: '1.6' }}>Sort results</p>
                                </div>
                                <div>
                                    <h5 style={{ color: '#1A2940', marginBottom: '0.5rem', fontSize: '1.1rem', fontWeight: 'bold' }}>LIMIT</h5>
                                    <p style={{ color: 'grey', fontSize: '0.95rem', margin: 0, lineHeight: '1.6' }}>Restrict number of results</p>
                                </div>
                                <div>
                                    <h5 style={{ color: '#1A2940', marginBottom: '0.5rem', fontSize: '1.1rem', fontWeight: 'bold' }}>COUNT()</h5>
                                    <p style={{ color: 'grey', fontSize: '0.95rem', margin: 0, lineHeight: '1.6' }}>Count number of rows</p>
                                </div>
                            </div>
                        </section>

                        {/* Tips and Best Practices */}
                        <section style={{ marginBottom: '4rem' }}>
                            <h2 style={{
                                fontSize: '2.25rem',
                                fontWeight: '700',
                                color: '#1A2940',
                                marginBottom: '2rem',
                                letterSpacing: '-0.01em'
                            }}>
                                Tips and Best Practices
                            </h2>
                            <ul style={{
                                color: 'grey',
                                fontSize: '1rem',
                                lineHeight: '2.2',
                                paddingLeft: '1.5rem',
                                listStyleType: 'none'
                            }}>
                                <li style={{ paddingLeft: '1.5rem', position: 'relative', marginBottom: '0.75rem' }}>
                                    <span style={{ position: 'absolute', left: 0, color: '#60a5fa', fontWeight: '700' }}>→</span>
                                    Use <strong style={{ color: '#1A2940', fontWeight: '600' }}>LIMIT</strong> when testing queries to avoid retrieving too much data
                                </li>
                                <li style={{ paddingLeft: '1.5rem', position: 'relative', marginBottom: '0.75rem' }}>
                                    <span style={{ position: 'absolute', left: 0, color: '#60a5fa', fontWeight: '700' }}>→</span>
                                    Column names and table names are case-sensitive in some databases
                                </li>
                                <li style={{ paddingLeft: '1.5rem', position: 'relative', marginBottom: '0.75rem' }}>
                                    <span style={{ position: 'absolute', left: 0, color: '#60a5fa', fontWeight: '700' }}>→</span>
                                    Use single quotes for string values: <code style={{
                                        backgroundColor: '#f1f5f9',
                                        padding: '0.3rem 0.6rem',
                                        borderRadius: '4px',
                                        fontSize: '0.9rem',
                                        color: '#334155'
                                    }}>WHERE gene = 'SCN1A'</code>
                                </li>
                                <li style={{ paddingLeft: '1.5rem', position: 'relative', marginBottom: '0.75rem' }}>
                                    <span style={{ position: 'absolute', left: 0, color: '#60a5fa', fontWeight: '700' }}>→</span>
                                    Comments can be added using <code style={{
                                        backgroundColor: '#f1f5f9',
                                        padding: '0.3rem 0.6rem',
                                        borderRadius: '4px',
                                        fontSize: '0.9rem',
                                        color: '#334155'
                                    }}>--</code> for single-line or <code style={{
                                        backgroundColor: '#f1f5f9',
                                        padding: '0.3rem 0.6rem',
                                        borderRadius: '4px',
                                        fontSize: '0.9rem',
                                        color: '#334155'
                                    }}>/* */</code> for multi-line
                                </li>
                                <li style={{ paddingLeft: '1.5rem', position: 'relative', marginBottom: '0.75rem' }}>
                                    <span style={{ position: 'absolute', left: 0, color: '#60a5fa', fontWeight: '700' }}>→</span>
                                    Test complex queries on a small dataset first before running on the entire database
                                </li>
                                <li style={{ paddingLeft: '1.5rem', position: 'relative', marginBottom: '0.75rem' }}>
                                    <span style={{ position: 'absolute', left: 0, color: '#60a5fa', fontWeight: '700' }}>→</span>
                                    Use meaningful aliases for tables to make complex queries more readable
                                </li>
                                <li style={{ paddingLeft: '1.5rem', position: 'relative' }}>
                                    <span style={{ position: 'absolute', left: 0, color: '#60a5fa', fontWeight: '700' }}>→</span>
                                    Always end SQL statements with a semicolon <code style={{
                                        backgroundColor: '#f1f5f9',
                                        padding: '0.3rem 0.6rem',
                                        borderRadius: '4px',
                                        fontSize: '0.9rem',
                                        color: '#334155'
                                    }}>;</code>
                                </li>
                            </ul>
                        </section>

                        {/* Get Help */}
                        <section>
                            <div style={{
                                backgroundColor: '#1A2940',
                                padding: '3rem',
                                textAlign: 'center',
                                borderRadius: '12px'
                            }}>
                                <h3 style={{ color: '#ffffff', marginBottom: '1rem', fontSize: '1.75rem', fontWeight: '700' }}>Need More Help?</h3>
                                <p style={{ color: '#94a3b8', fontSize: '1.05rem', marginBottom: '2rem', lineHeight: '1.6' }}>
                                    Visit our FAQ page for more questions and answers about using SQL with MintDB.
                                </p>
                                <a
                                    href="/faq#sql-queries"
                                    style={{
                                        display: 'inline-block',
                                        backgroundColor: '#60a5fa',
                                        color: 'white',
                                        padding: '1rem 2.5rem',
                                        borderRadius: '8px',
                                        textDecoration: 'none',
                                        fontWeight: '600',
                                        fontSize: '1rem',
                                        transition: 'all 0.2s',
                                        border: '2px solid transparent'
                                    }}
                                    onMouseEnter={(e) => {
                                        e.currentTarget.style.backgroundColor = '#3b82f6';
                                        e.currentTarget.style.transform = 'translateY(-2px)';
                                    }}
                                    onMouseLeave={(e) => {
                                        e.currentTarget.style.backgroundColor = '#60a5fa';
                                        e.currentTarget.style.transform = 'translateY(0)';
                                    }}
                                >
                                    View SQL FAQ
                                </a>
                            </div>
                        </section>

                    </div>
                </div>
            </div>
        </div>
    );
}

export default SQLGuide;
